// ============================================================
// attack/ram - a vehicle's ram, resolved (plan map-display, Track D).
//
// The GM has ruled the damage (engine/actions/ram: no house rule computes
// it). The strike is the ordinary hit path with a stand-in weapon - the
// GM's number, Impact, never parried - so the target's Dodge, its armour by
// the facing it is struck on, the log and the display's beat all come with
// it. It cannot miss: the driver drove into them, and only the Dodge stands
// between. It is ONE hit: nothing of the attacker's own adds to it - no fire
// mode, no talent's melee damage, no Charge or All-Out - because the GM's
// number IS the damage. The bow's share rides the weapon (`ramBow`) and is
// taken where the hit lands (useApplyHits): a Dodged ram costs the bow nothing.
// ============================================================
import { ramSlot, type RamRuling } from '../../engine/actions/ram';
import { armSwing, hitFrom } from './armed';
import { settleAttack } from './outcome';
import { resumeAttackFromHit } from './shot';
import { promptOpen, refusalReason } from './swing';
import type { AttackContext } from './context';
import type { NarrativeEntry, Tab } from '../../types';

/** A line in the target's log. */
const info = (text: string): NarrativeEntry => ({ type: 'info', text, timestamp: Date.now() });

/** Who the GM was asked about: the ram strikes them or nobody. */
export interface RamPair {
  actorId: string | null;
  targetId: string | null;
}

/** The context a ram strikes in: the stand-in weapon in hand, one shot, the actor's own bonuses left out. */
function rammingContext(ctx: AttackContext, ruling: RamRuling): AttackContext {
  return {
    ...ctx,
    currentSlots: [ramSlot(ruling.damage, ruling.bow)],
    selectedSlot: 0,
    fireMode: 'single',
    suppressing: false,
    weaponOverrides: null,
    attackBlockedReason: null,
    attackerCharStats: ctx.attackerCharStats ? { ...ctx.attackerCharStats, damageBonusMelee: 0 } : null,
    isAllOutAttack: false,
    isGuardedAttack: false,
    isCharge: false,
    ramming: true,
    isGangUp: false,
    hasBerserkCharge: false,
    hasHammerBlow: false,
    isCalledShot: false,
    hatred: false,
    rerollDamage: false,
  };
}

/**
 * Why this ram may not strike now, or null: the pair changed while the GM
 * ruled, or what refuses any attack (swing.ts) - a prompt open, a lock, a
 * defence pending on the target.
 */
function ramRefusal(ctx: AttackContext, pair: RamPair): string | null {
  if (!ctx.currentTab || ctx.activeTab !== pair.targetId || (ctx.actorId ?? null) !== pair.actorId) {
    return '🔴 The ram was ruled for another target or driver: nothing is struck.';
  }
  if (promptOpen(ctx)) return '🔴 A prompt is still open: the ram waits.';
  if (ctx.isLocked('attack')) return '🔴 Attacks are locked: no ram.';
  return refusalReason({ ...ctx, attackBlockedReason: null });
}

/**
 * Resolve a ram with the GM's ruling on the pair it was asked about. A
 * refusal, or anything thrown, is a line in the log - never lost.
 */
export function handleRam(ctx: AttackContext, ruling: RamRuling, pair: RamPair): void {
  const refused = ramRefusal(ctx, pair);
  if (refused) {
    if (ctx.activeTab) ctx.addNarrative(ctx.activeTab, info(refused));
    return;
  }
  try {
    const tab = ctx.currentTab as Tab;
    const rammed = rammingContext(ctx, ruling);
    const sw = armSwing(rammed, tab, 0, rammed.computeCountdown(rammed.activeTab));
    if (!sw) return;
    sw.notes.push(`Ram: the GM rules ${ruling.damage} to the target, ${ruling.bow} to the bow.`);
    resumeAttackFromHit(rammed, hitFrom(rammed, sw, 0));
    rammed.historyTrack.current = true;
    settleAttack(rammed, tab.id, { hit: true, slot: sw.slot, isMelee: true });
  } catch (err) {
    ctx.addNarrative(ctx.activeTab, info('⚠ Ram error: ' + (err instanceof Error ? err.message : String(err))));
  }
}
